import React, { useEffect, useState } from "react";
import ResourceEditor from "./ResourceEditor";
import ImageUpload from "./ImageUpload";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Save, CheckCircle2 } from "lucide-react";

export const CategoriesAdmin = () => (
  <ResourceEditor
    resource="categories"
    title="Catégories de cours"
    subtitle="Créez les catégories ici avant d'ajouter les cours correspondants (ex : Classique, Modern Jazz, Éveil à la danse)."
    fields={[
      { key: "name", label: "Nom de la catégorie", placeholder: "Ex: Classique", full: true },
    ]}
    emptyItem={{ name: "" }}
    renderPreview={(c) => (
      <div className="min-w-0">
        <div className="font-serif-display text-stone-800">{c.name}</div>
      </div>
    )}
  />
);

export const CoursesAdmin = () => {
  const [categories, setCategories] = useState([]);
  const [teachers, setTeachers] = useState([]);
  useEffect(() => {
    api.get("/admin/categories").then(({ data }) => setCategories(data || [])).catch(() => {});
    api.get("/admin/teachers").then(({ data }) => setTeachers(data || [])).catch(() => {});
  }, []);
  const categoryOptions = categories.map((c) => ({ value: c.name, label: c.name }));
  if (categories.length === 0) {
    return (
      <div>
        <h1 className="font-serif-display text-2xl sm:text-3xl text-stone-800">Cours</h1>
        <div className="mt-6 bg-amber-50 border-2 border-amber-200 rounded-2xl p-6 text-amber-800">
          <p className="font-serif-display text-lg">Aucune catégorie créée pour le moment.</p>
          <p className="text-sm mt-2">Avant d&apos;ajouter des cours, créez d&apos;abord vos catégories dans le menu « Catégories de cours ».</p>
          <a href="/admin/categories" className="inline-block mt-4 bg-coral text-white px-5 py-2 rounded-full text-sm tracking-widest uppercase">Créer une catégorie</a>
        </div>
      </div>
    );
  }
  return (
    <ResourceEditor
      resource="courses"
      title="Cours"
      subtitle="Gérer les cours présentés sur le site. Un cours peut avoir plusieurs créneaux dans la semaine."
      groupByField="category"
      fields={[
        { key: "category", label: "Catégorie *", type: "select", options: categoryOptions },
        { key: "name", label: "Nom du cours *", placeholder: "Ex: Initiation" },
        { key: "age", label: "Tranche d'âge", placeholder: "Ex: 6 - 7 ans" },
        { key: "duration", label: "Durée", placeholder: "Ex: 1h" },
        { key: "color", label: "Couleur (classe Tailwind)", placeholder: "bg-rose-100" },
        { key: "maxStudents", label: "Nb max d'élèves (0 = illimité)", type: "number", placeholder: "0" },
        { key: "image", label: "Image", type: "image", placeholder: "https://... ou téléverser", full: true },
        { key: "desc", label: "Description", type: "textarea", full: true },
        { key: "slots", label: "Créneaux dans la semaine", type: "slots", teachers: teachers, full: true },
      ]}
      emptyItem={{ name: "", category: categories[0]?.name || "", age: "", duration: "", desc: "", image: "", color: "bg-rose-100", maxStudents: 0, slots: [] }}
      renderPreview={(c) => (
        <div className="flex items-center gap-3 min-w-0">
          {c.image && <img src={c.image} alt={c.name} className="w-14 h-14 rounded-lg object-cover shrink-0" />}
          <div className="min-w-0 flex-1">
            <div className="font-serif-display text-stone-800 truncate">{c.name} <span className="text-stone-400 text-sm">· {c.age}</span></div>
            <div className="text-stone-500 text-sm truncate">{c.desc}</div>
            <div className="flex flex-wrap gap-2 text-xs mt-0.5">
              {c.maxStudents > 0 && <span className="text-amber-700">Capacité : {c.maxStudents}</span>}
              {Array.isArray(c.slots) && c.slots.length > 0 && <span className="text-stone-500">{c.slots.length} créneau{c.slots.length > 1 ? "x" : ""}</span>}
            </div>
          </div>
        </div>
      )}
    />
  );
};

export const TeachersAdmin = () => (
  <ResourceEditor
    resource="teachers"
    title="Professeurs"
    subtitle="Fiches publiques affichées sur le site (photo, bio, citation). Pour donner un accès BO/espace prof, allez dans Administrateurs et liez le compte à ce professeur."
    fields={[
      { key: "name", label: "Prénom *", placeholder: "Ex: Jessica" },
      { key: "discipline", label: "Discipline", placeholder: "Ex: Danse classique" },
      { key: "color", label: "Couleur (classe Tailwind)", placeholder: "bg-orange-500" },
      { key: "photo", label: "Photo", type: "image", placeholder: "https://... ou téléverser", full: true },
      { key: "bio", label: "Biographie", type: "textarea", rows: 5, full: true },
      { key: "quote", label: "Citation", type: "textarea", rows: 2, full: true },
    ]}
    emptyItem={{ name: "", discipline: "", color: "bg-orange-500", photo: "", bio: "", quote: "" }}
    renderPreview={(t) => (
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {t.photo
          ? <img src={t.photo} alt={t.name} className="w-14 h-14 rounded-full object-cover shrink-0" />
          : <div className={`w-14 h-14 rounded-full shrink-0 ${t.color || "bg-stone-200"} flex items-center justify-center text-white font-serif-display text-lg`}>{(t.name || "?").slice(0,1)}</div>
        }
        <div className="min-w-0 flex-1">
          <div className="font-serif-display text-stone-800 truncate">{t.name}</div>
          <div className="text-stone-500 text-sm truncate">{t.discipline}</div>
          {t.isAdmin && (
            <div className="text-xs mt-0.5 text-emerald-700 inline-flex items-center gap-1">
              <CheckCircle2 size={12}/> Compte admin lié
            </div>
          )}
        </div>
      </div>
    )}
  />
);

export const TarifsAdmin = () => (
  <ResourceEditor
    resource="tarifs"
    title="Tarifs"
    subtitle="Lignes du tableau des tarifs."
    fields={[
      { key: "course", label: "Discipline / Durée", placeholder: "Ex: Initiation (1h)", full: true },
      { key: "year", label: "Tarif annuel", placeholder: "Ex: 360 €" },
      { key: "trimester", label: "Tarif trimestre", placeholder: "Ex: 135 €" },
    ]}
    emptyItem={{ course: "", year: "", trimester: "" }}
    renderPreview={(t) => (
      <div>
        <div className="font-serif-display text-stone-800">{t.course}</div>
        <div className="text-stone-500 text-sm">{t.year} <span className="mx-2">·</span> {t.trimester}</div>
      </div>
    )}
  />
);

export const TenuesAdmin = () => (
  <ResourceEditor
    resource="tenues"
    title="Tenues"
    subtitle="Tenues recommandées par discipline."
    fields={[
      { key: "course", label: "Discipline", placeholder: "Ex: Initiation", full: true },
      { key: "items", label: "Détail de la tenue", type: "textarea", full: true },
      { key: "orderUrl", label: "Fiche de commande (URL ou document)", type: "document", placeholder: "https://… ou téléverser un PDF", full: true },
      { key: "orderLabel", label: "Libellé du bouton", placeholder: "Commander" },
    ]}
    emptyItem={{ course: "", items: "", orderUrl: "", orderLabel: "Commander" }}
    renderPreview={(t) => (
      <div className="min-w-0">
        <div className="font-serif-display text-stone-800">{t.course}</div>
        <div className="text-stone-500 text-sm truncate">{t.items}</div>
        {t.orderUrl && (
          <div className="text-emerald-700 text-xs mt-0.5 inline-flex items-center gap-1">
            ↗ {t.orderLabel || "Commander"} : <span className="truncate">{t.orderUrl}</span>
          </div>
        )}
      </div>
    )}
  />
);

export const DatesAdmin = () => (
  <ResourceEditor
    resource="dates"
    title="Dates importantes"
    subtitle="Rentrée, vacances, portes ouvertes, spectacle…"
    fields={[
      { key: "label", label: "Libellé", placeholder: "Ex: Rentrée", full: true },
      { key: "value", label: "Date / détail", placeholder: "Ex: Lundi 8 septembre 2026", full: true },
    ]}
    emptyItem={{ label: "", value: "" }}
  />
);

export const StagesAdmin = () => (
  <ResourceEditor
    resource="stages"
    title="Stages de vacances"
    subtitle="Ateliers ponctuels (vacances scolaires, week-ends). Les stages ne facturent PAS la licence ni la cotisation asso — uniquement le prix du stage + les frais de dossier."
    fields={[
      { key: "title", label: "Titre *", placeholder: "Ex: Stage Modern-Jazz Toussaint 2026" },
      { key: "dates", label: "Dates (texte libre)", placeholder: "Ex: Lundi 20 → Mercredi 22 oct.", full: true },
      { key: "description", label: "Description", type: "textarea", full: true },
      { key: "price", label: "Prix du stage (€) *", type: "number", placeholder: "80" },
      { key: "maxStudents", label: "Capacité max (0 = illimitée)", type: "number", placeholder: "12" },
      { key: "ageRange", label: "Tranche d'âge", placeholder: "Ex: 8-12 ans" },
      { key: "teacherName", label: "Professeur", placeholder: "Ex: Chloé" },
      { key: "image", label: "Image", type: "image", full: true },
    ]}
    emptyItem={{ title: "", dates: "", description: "", price: 0, maxStudents: 0, ageRange: "", teacherName: "", image: "" }}
    renderPreview={(s) => (
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {s.image && <img src={s.image} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className="font-serif-display text-stone-800 truncate">{s.title}</div>
          <div className="text-stone-500 text-xs truncate">{s.dates} · {s.price ? `${Number(s.price).toFixed(0)} €` : "prix ?"}</div>
        </div>
      </div>
    )}
  />
);

export const BoardMembersAdmin = () => (
  <ResourceEditor
    resource="board_members"
    title="Bureau de l'association"
    subtitle="Membres du bureau affichés en bas de la page Contact (photo, nom, rôle)."
    fields={[
      { key: "name", label: "Nom complet *", placeholder: "Ex: Rosa ALVES DA SILVA" },
      { key: "role", label: "Rôle *", placeholder: "Ex: Présidente" },
      { key: "photo", label: "Photo", type: "image", placeholder: "https://... ou téléverser", full: true },
    ]}
    emptyItem={{ name: "", role: "", photo: "" }}
    renderPreview={(m) => (
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {m.photo
          ? <img src={m.photo} alt={m.name} className="w-14 h-14 rounded-full object-cover shrink-0" />
          : <div className="w-14 h-14 rounded-full shrink-0 bg-blush-light text-deep-rose flex items-center justify-center font-serif-display text-lg">{(m.name || "?").slice(0, 1)}</div>
        }
        <div className="min-w-0 flex-1">
          <div className="font-serif-display text-stone-800 truncate">{m.name}</div>
          <div className="text-stone-500 text-sm truncate">{m.role}</div>
        </div>
      </div>
    )}
  />
);

export const GalleryCategoriesAdmin = () => (
  <ResourceEditor
    resource="gallery_categories"
    title="Catégories de galerie"
    subtitle="Créez ici les rubriques qui apparaîtront comme filtres sur la page publique Galerie (ex : Gala 2025, Studio, Coulisses, Classiques…)."
    fields={[
      { key: "name", label: "Nom de la catégorie", placeholder: "Ex: Gala 2025", full: true },
    ]}
    emptyItem={{ name: "" }}
    renderPreview={(c) => (
      <div className="min-w-0">
        <div className="font-serif-display text-stone-800">{c.name}</div>
      </div>
    )}
  />
);

export const GalleryAdmin = () => {
  const [galleryCategories, setGalleryCategories] = useState([]);
  useEffect(() => {
    api.get("/admin/gallery_categories").then(({ data }) => setGalleryCategories(data || [])).catch(() => {});
  }, []);
  const categoryOptions = [{ value: "", label: "— Aucune —" }, ...galleryCategories.map((c) => ({ value: c.name, label: c.name }))];
  return (
    <ResourceEditor
      resource="gallery"
      title="Galerie photos"
      subtitle="Liste des images affichées sur la page Galerie. Utilisez les catégories pour permettre aux visiteurs de filtrer."
      groupByField="category"
      fields={[
        { key: "url", label: "Image", type: "image", placeholder: "https://... ou téléverser", full: true },
        { key: "caption", label: "Légende (optionnel)", placeholder: "", full: true },
        { key: "category", label: "Catégorie", type: "select", options: categoryOptions, full: true },
      ]}
      emptyItem={{ url: "", caption: "", category: "" }}
      renderPreview={(g) => (
        <div className="flex items-center gap-3 min-w-0">
          {g.url && <img src={g.url} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />}
          <div className="min-w-0 flex-1">
            <div className="text-stone-700 text-sm truncate">{g.caption || g.url}</div>
            {g.category && <div className="text-xs text-deep-rose mt-0.5">{g.category}</div>}
          </div>
        </div>
      )}
    />
  );
};


const COLOR_OPTIONS = [
  { value: "bg-blush-light", label: "Rose poudré" },
  { value: "bg-sky-soft", label: "Bleu pâle" },
  { value: "bg-cream-yellow", label: "Jaune crème" },
  { value: "bg-peach", label: "Pêche" },
  { value: "bg-mauve/40", label: "Mauve clair" },
];

const LINK_OPTIONS = [
  { value: "/contact", label: "Page Contact" },
  { value: "/dates-importantes", label: "Page Dates importantes" },
  { value: "/cours-tenues", label: "Page Cours & Tenues" },
  { value: "/planning-tarifs", label: "Page Planning & Tarifs" },
  { value: "/inscription", label: "Page Inscription" },
  { value: "/galerie", label: "Page Galerie" },
  { value: "/", label: "Page d'accueil" },
];

export const InfosAdmin = () => (
  <ResourceEditor
    resource="infos"
    title="Informations importantes"
    subtitle="Bannières colorées affichées sur la page d'accueil."
    fields={[
      { key: "text", label: "Message", type: "textarea", rows: 3, full: true, placeholder: "Texte de l'information" },
      { key: "cta", label: "Texte du bouton", placeholder: "Ex: En savoir plus" },
      { key: "link", label: "Lien du bouton", type: "select", options: LINK_OPTIONS },
      { key: "color", label: "Couleur de fond", type: "select", options: COLOR_OPTIONS, full: true },
    ]}
    emptyItem={{ text: "", cta: "En savoir plus", link: "/contact", color: "bg-blush-light" }}
    renderPreview={(info) => (
      <div className="min-w-0">
        <div className={`inline-block w-3 h-3 rounded-full ${info.color || "bg-stone-300"} mr-2 align-middle`} />
        <span className="font-serif-display text-stone-800 truncate align-middle">{info.text || "(vide)"}</span>
        <div className="text-stone-500 text-xs mt-1 truncate">Bouton : « {info.cta} » → {info.link}</div>
      </div>
    )}
  />
);
